import { Component, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

export interface FaqItem {
  id: string;
  category: string;
  question: string;
  answer: string;
  youtubeVideoId?: string;
  safeVideoUrl?: SafeResourceUrl;
  expanded?: boolean;
}

@Component({
  selector: 'app-help',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './help.html',
  styleUrl: './help.scss'
})
export class Help {
  private sanitizer = inject(DomSanitizer);

  public searchQuery = signal<string>('');
  public selectedCategory = signal<string>('All');

  public categories = signal<string[]>(['All', 'Profile & Cosmetics', 'Gameplay & Rewards', 'Account & Settings']);

  public faqList = signal<FaqItem[]>([
    {
      id: 'portrait-nameplate',
      category: 'Profile & Cosmetics',
      question: 'Where do I go to change my portrait or name plate?',
      answer: 'You can easily customize your player profile card by navigating to your Profile. Click your avatar or the Profile icon in the left menu, then select "Change Avatar" or "Change Nameplate" to equip owned cosmetics. Watch the short video guide below for a complete walkthrough:',
      youtubeVideoId: 'ZUxaY0gNA7U',
      expanded: true
    },
    {
      id: 'spades-nameplates',
      category: 'Gameplay & Rewards',
      question: 'How do I unlock Spades Victory Nameplates (10+, 50+, 100+)?',
      answer: 'Spades victory nameplates are exclusive rewards earned by winning Spades matches! Once you reach 10, 50, or 100 wins, the corresponding animated nameplate will automatically unlock in your collection.',
      expanded: false
    },
    {
      id: 'game-queues',
      category: 'Gameplay & Rewards',
      question: 'How do game lobbies and queues work?',
      answer: 'You can browse available lobbies or start your own from the Home Overview tab. Choose your preferred game mode (Spades, Backgammon, Canasta), invite friends, or open your lobby to public players.',
      expanded: false
    },
    {
      id: 'settings-customization',
      category: 'Account & Settings',
      question: 'How do I adjust audio, notifications, or app preferences?',
      answer: 'Click the Gear icon at the bottom of the left sidebar to open the Settings modal. From there, you can toggle sound effects, background music, chat notifications, and graphic quality.',
      expanded: false
    },
    {
      id: 'rankings-tier',
      category: 'Gameplay & Rewards',
      question: 'How does the Leaderboard and Ranking system work?',
      answer: 'Your ELO rating increases as you win matches. Tiers range from Bronze to Diamond and Elite. Check the Rankings page to view your current tier standing and top global players.',
      expanded: false
    }
  ]);

  public processedFaqs = computed(() => {
    const list = this.faqList();
    return list.map(item => {
      if (item.youtubeVideoId && !item.safeVideoUrl) {
        item.safeVideoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          `https://www.youtube.com/embed/${item.youtubeVideoId}`
        );
      }
      return item;
    });
  });

  public filteredFaqs = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    const cat = this.selectedCategory();
    let items = this.processedFaqs();

    if (cat !== 'All') {
      items = items.filter(i => i.category === cat);
    }

    if (query) {
      items = items.filter(i =>
        i.question.toLowerCase().includes(query) ||
        i.answer.toLowerCase().includes(query)
      );
    }

    return items;
  });

  toggleFaq(item: FaqItem) {
    this.faqList.update(list =>
      list.map(f => f.id === item.id ? { ...f, expanded: !f.expanded } : f)
    );
  }

  selectCategory(category: string) {
    this.selectedCategory.set(category);
  }
}
